/**
 * Landing page — primary content. Agent-first quickstart, links to Swagger.
 */
import { useEffect, useState } from "react";

import type { VersionInfo } from "../types";

const CURL_QUICKSTART = `# 1. Sign up — public, no admin needed (rate limit 5/min, 50/hr per IP)
TOKEN=$(curl -s -X POST -H 'content-type: application/json' \\
  -d '{}' \\
  https://mindmap.parklab.work/v1/signup | jq -r .token.token)

# 2. Create a mindmap (Bearer required)
M=$(curl -s -X POST -H "Authorization: Bearer $TOKEN" \\
  -H 'content-type: application/json' \\
  -d '{"name":"q3 planning","root_title":"Q3"}' \\
  https://mindmap.parklab.work/v1/mindmaps)
echo "$M" | jq .edit_url
# → "https://mindmap.parklab.work/e/<uuid7>"  ← share this link

MID=$(echo "$M" | jq -r .id)
RID=$(echo "$M" | jq -r .root_node_id)

# 3. Add nodes (NO bearer — the mindmap_id is the capability)
curl -s -X POST -H 'content-type: application/json' \\
  -d "{\\"parent_id\\":\\"$RID\\",\\"title\\":\\"hire 2 engineers\\"}" \\
  https://mindmap.parklab.work/v1/mindmaps/$MID/nodes

# 4. Check off a node (If-Match for optimistic concurrency)
NID=$(curl -s https://mindmap.parklab.work/v1/mindmaps/$MID/tree \\
  | jq -r '.tree.children[0].id')
curl -s -X PATCH -H 'content-type: application/json' \\
  -H 'If-Match: 1' \\
  -d '{"checked":true}' \\
  https://mindmap.parklab.work/v1/nodes/$NID

# 5. Read the whole tree (with aggregated agg_state)
curl -s https://mindmap.parklab.work/v1/mindmaps/$MID/tree | jq`;

const PY_QUICKSTART = `import httpx

BASE = "https://mindmap.parklab.work"

with httpx.Client(base_url=BASE) as c:
    # 1. Sign up — public
    signup = c.post("/v1/signup", json={}).json()
    token = signup["token"]["token"]

    # 2. Create a mindmap (Bearer required)
    m = c.post(
        "/v1/mindmaps",
        headers={"Authorization": f"Bearer {token}"},
        json={"name": "q3 planning", "root_title": "Q3"},
    ).json()
    print("share with humans:", m["edit_url"])

    # 3. Add a child node (no auth needed — mindmap_id is the cap)
    n = c.post(
        f"/v1/mindmaps/{m['id']}/nodes",
        json={"parent_id": m["root_node_id"], "title": "hire 2 engineers"},
    ).json()

    # 4. Toggle checked
    c.patch(
        f"/v1/nodes/{n['id']}",
        headers={"If-Match": str(n["version"])},
        json={"checked": True},
    )

    # 5. Read full tree
    tree = c.get(f"/v1/mindmaps/{m['id']}/tree").json()
    print(tree["tree"]["agg_state"])`;

type Tab = "curl" | "python";

export function Landing() {
  const [tab, setTab] = useState<Tab>("curl");
  const [version, setVersion] = useState<VersionInfo | null>(null);
  const snippet = tab === "curl" ? CURL_QUICKSTART : PY_QUICKSTART;

  useEffect(() => {
    fetch("/v1/version")
      .then((r) => r.json())
      .then((v) => setVersion(v as VersionInfo))
      .catch(() => undefined);
  }, []);
  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", color: "#0f172a", background: "#fff", minHeight: "100vh" }}>
      <header style={{ background: "#0f172a", color: "#e2e8f0", padding: "32px 0" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px" }}>
          <h1 style={{ margin: 0, fontSize: 32, fontWeight: 600 }}>mindmap.parklab.work</h1>
          <p style={{ margin: "8px 0 0", color: "#94a3b8", fontSize: 16 }}>
            Agent-first mindmap + todo. Agents create; humans and agents both edit through the same data model.
          </p>
          <div style={{ marginTop: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <a href="/docs" style={btn}>Swagger UI</a>
            <a href="/redoc" style={btnSecondary}>ReDoc</a>
            <a href="/openapi.json" style={btnSecondary}>openapi.json</a>
            <a href="https://github.com/ffee21/mindmap-todo" style={btnSecondary} target="_blank" rel="noreferrer">GitHub</a>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 900, margin: "0 auto", padding: "32px 24px" }}>
        <Section title="How it works">
          <ol style={listStyle}>
            <li><strong>Agent signs up</strong> via <code>POST /v1/signup</code> (no admin token, no email — just an IP-rate-limited request). The response contains a bearer token.</li>
            <li><strong>Agent creates a mindmap</strong> via <code>POST /v1/mindmaps</code> with that bearer token. The response contains an <code>edit_url</code> like <code>/e/&lt;uuid7&gt;</code>.</li>
            <li><strong>Agent shares the URL</strong> with a human. The mindmap id is a 122-bit unguessable capability.</li>
            <li><strong>Both edit the same tree.</strong> Humans use the in-browser WYSIWYG editor at <code>/e/&lt;id&gt;</code>. Agents call <code>PATCH /v1/nodes/&lt;id&gt;</code>, <code>POST /v1/nodes/&lt;id&gt;/move</code>, etc. — no Bearer needed once the id is known.</li>
            <li><strong>Aggregation:</strong> each non-leaf node shows <code>checked | mixed | unchecked</code> derived from its descendants.</li>
            <li><strong>Audit:</strong> every mutation is logged to <code>/v1/mindmaps/&lt;id&gt;/events</code> (append-only).</li>
          </ol>
        </Section>

        <Section title="Quick start">
          <div style={{ marginBottom: 8, display: "flex", gap: 4 }}>
            <button style={tab === "curl" ? tabBtnActive : tabBtn} onClick={() => setTab("curl")}>curl</button>
            <button style={tab === "python" ? tabBtnActive : tabBtn} onClick={() => setTab("python")}>python (httpx)</button>
          </div>
          <pre style={pre}>{snippet}</pre>
        </Section>

        <Section title="Auth model at a glance">
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Endpoint</th>
                <th style={th}>Auth</th>
                <th style={th}>Who uses it</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={td}><code>POST /v1/signup</code></td><td style={td}>— (rate-limited)</td><td style={td}>Anyone (any agent, any human)</td></tr>
              <tr><td style={td}><code>POST /v1/admin/accounts</code></td><td style={td}>Admin token</td><td style={td}>Operator (out-of-band)</td></tr>
              <tr><td style={td}><code>POST /v1/admin/accounts/{"{id}"}/tokens</code></td><td style={td}>Admin token</td><td style={td}>Operator</td></tr>
              <tr><td style={td}><code>POST /v1/mindmaps</code></td><td style={td}>Bearer</td><td style={td}>Agent</td></tr>
              <tr><td style={td}><code>GET /v1/mindmaps</code></td><td style={td}>Bearer</td><td style={td}>Agent (list own)</td></tr>
              <tr><td style={td}><code>GET /v1/mindmaps/{"{id}"}/tree</code></td><td style={td}>—</td><td style={td}>Human, agent</td></tr>
              <tr><td style={td}><code>POST /v1/mindmaps/{"{id}"}/nodes</code></td><td style={td}>—</td><td style={td}>Human, agent</td></tr>
              <tr><td style={td}><code>PATCH /v1/nodes/{"{id}"}</code></td><td style={td}>If-Match required</td><td style={td}>Human, agent</td></tr>
              <tr><td style={td}><code>POST /v1/nodes/{"{id}"}/move</code></td><td style={td}>If-Match required</td><td style={td}>Human, agent</td></tr>
              <tr><td style={td}><code>DELETE /v1/nodes/{"{id}"}</code></td><td style={td}>If-Match required</td><td style={td}>Human, agent</td></tr>
              <tr><td style={td}><code>GET /v1/mindmaps/{"{id}"}/events</code></td><td style={td}>—</td><td style={td}>Anyone with the id</td></tr>
              <tr><td style={td}><code>POST /v1/suggestions</code></td><td style={td}>— (rate-limited; Bearer optional)</td><td style={td}>Anyone — feature requests for review</td></tr>
              <tr><td style={td}><code>GET /v1/suggestions</code></td><td style={td}>—</td><td style={td}>Anyone — browse what's been suggested</td></tr>
              <tr><td style={td}><code>POST /v1/suggestions/{"{id}"}/comments</code></td><td style={td}>— (rate-limited; Bearer optional)</td><td style={td}>Anyone — comment on a suggestion</td></tr>
              <tr><td style={td}><code>GET /v1/suggestions/{"{id}"}/comments</code></td><td style={td}>—</td><td style={td}>Anyone — read the thread</td></tr>
              <tr><td style={td}><code>GET /v1/version</code></td><td style={td}>—</td><td style={td}>Anyone — what's live (version, git_sha, built_at)</td></tr>
            </tbody>
          </table>
          <p style={{ fontSize: 13, color: "#64748b", marginTop: 12 }}>
            <strong>Capability URL.</strong> The mindmap id (uuid7) is unguessable; sharing the URL = granting full edit access. Leaked URL = compromise. There is no per-link revocation in v0 — delete the mindmap and create a new one.
          </p>
        </Section>

        <Section title="Already have a mindmap URL?">
          <p style={{ fontSize: 14, color: "#475569" }}>
            Just open it: <code>https://mindmap.parklab.work/e/&lt;your-uuid7&gt;</code>
          </p>
        </Section>

        <Section title="Suggest a feature">
          <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.6 }}>
            Not every suggestion gets shipped, but every one is recorded for review when
            planning future development. Anyone — agent or human — can submit:
          </p>
          <pre style={pre}>{`# Public, no auth needed (rate-limited 3/min, 10/hr per IP)
curl -X POST -H 'content-type: application/json' \\
  -d '{"title":"add markmap export","body":"would help with sharing to non-users"}' \\
  https://mindmap.parklab.work/v1/suggestions

# Browse what others suggested
curl https://mindmap.parklab.work/v1/suggestions | jq

# Filter by status: open | considering | accepted | declined | shipped
curl 'https://mindmap.parklab.work/v1/suggestions?status=accepted' | jq

# Add a comment to a suggestion (+1 / additional context / questions)
curl -X POST -H 'content-type: application/json' \\
  -d '{"body":"+1, blocked on this for sharing"}' \\
  https://mindmap.parklab.work/v1/suggestions/<id>/comments

# Read the thread on a suggestion (oldest first)
curl https://mindmap.parklab.work/v1/suggestions/<id>/comments | jq`}</pre>
          <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 8 }}>
            A daily autonomous reviewer (runs at 09:00 KST) classifies each open suggestion
            and either ships small low-risk picks, declines off-topic ones, or marks them
            for human consideration — leaving a comment explaining the decision either way.
            Polling the comment thread is the cheapest way to track what happened to your idea.
            Bearer is optional; including it attaches your account so we can follow up.
          </p>
        </Section>

        <footer style={{ marginTop: 48, paddingTop: 16, borderTop: "1px solid #e2e8f0", color: "#94a3b8", fontSize: 12 }}>
          <div>
            <a href="https://github.com/ffee21/mindmap-todo" style={{ color: "#64748b" }} target="_blank" rel="noreferrer">source on GitHub</a>
            {" · "}<a href="https://github.com/ffee21/mindmap-todo/blob/main/CHANGELOG.md" style={{ color: "#64748b" }} target="_blank" rel="noreferrer">changelog</a>
            {" · "}data: PostgreSQL + adjacency list + lexorank
            {" · "}stack: FastAPI · React Flow · dagre
          </div>
          {version && (
            <div style={{ marginTop: 6 }}>
              live: <code>v{version.version}</code>
              {version.git_sha !== "unknown" && (
                <>
                  {" · "}
                  <a
                    href={`https://github.com/ffee21/mindmap-todo/commit/${version.git_sha}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "#64748b" }}
                  >
                    <code>{version.git_sha.slice(0, 7)}</code>
                  </a>
                </>
              )}
              {version.built_at !== "unknown" && <> · built {version.built_at}</>}
            </div>
          )}
        </footer>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12, borderBottom: "1px solid #e2e8f0", paddingBottom: 6 }}>{title}</h2>
      {children}
    </section>
  );
}

const btn: React.CSSProperties = {
  display: "inline-block",
  padding: "8px 16px",
  background: "#2563eb",
  color: "#fff",
  borderRadius: 4,
  textDecoration: "none",
  fontSize: 14,
};

const btnSecondary: React.CSSProperties = {
  ...btn,
  background: "transparent",
  border: "1px solid #475569",
  color: "#e2e8f0",
};

const tabBtn: React.CSSProperties = {
  padding: "6px 14px",
  background: "#f1f5f9",
  border: "1px solid #cbd5e1",
  borderRadius: "4px 4px 0 0",
  cursor: "pointer",
  fontSize: 13,
};

const tabBtnActive: React.CSSProperties = {
  ...tabBtn,
  background: "#0f172a",
  color: "#fff",
  borderColor: "#0f172a",
};

const pre: React.CSSProperties = {
  background: "#0f172a",
  color: "#e2e8f0",
  padding: 16,
  borderRadius: "0 4px 4px 4px",
  overflowX: "auto",
  fontSize: 12.5,
  lineHeight: 1.5,
  fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
};

const listStyle: React.CSSProperties = {
  paddingLeft: 20,
  lineHeight: 1.7,
  color: "#334155",
};

const table: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "2px solid #cbd5e1",
  background: "#f8fafc",
  fontWeight: 600,
};

const td: React.CSSProperties = {
  padding: "8px 10px",
  borderBottom: "1px solid #e2e8f0",
};
